import {
  Box,
  Card,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import EnergyReportPanel from "./EnergyReportPanel";
import { PageHeader } from "./PageHeader";
import { foyerApi } from "../http/foyer";
import type { EnergyRange, EnergyReport } from "@shared/types/foyer";

const RANGE_OPTIONS: { label: string; value: EnergyRange }[] = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
];

/**
 * Premise-scoped energy + CO₂ overview, shown inside the premise editor next to
 * the power schedules that drive the savings. This is the customer-facing ESG
 * surface - a hotel sees its own building's consumption, not a fleet-wide
 * total. Reads the energy endpoint narrowed to this premise.
 */
export default function EnergyView({ premiseId }: { premiseId: string }) {
  const [data, setData] = useState<EnergyReport | null>(null);
  const [range, setRange] = useState<EnergyRange>("week");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await foyerApi.fleetEnergy(range, { premiseId });
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      }
    }
    load();
    const t = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [range, premiseId]);

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="TV"
        title="Modeled energy"
        help="Estimated consumption and CO₂ for this property - the ESG story your power schedules drive. Modelled from device draw, not metered at the wall."
        actions={
          <Select
            size="small"
            value={range}
            onChange={(e) => setRange(e.target.value as EnergyRange)}
            sx={{ minWidth: 130, bgcolor: "background.paper" }}
            data-testid="energy-range"
          >
            {RANGE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        }
      />

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {data ? (
        <EnergyReportPanel report={data} scope="premise" />
      ) : (
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ p: 3 }}>
                <Box sx={{ height: 88 }} />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}
